import { useState, useEffect } from "react";
import { axiosInstance } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Building2, DollarSign, TrendingUp, Calendar } from "lucide-react";

export default function ConsolidatedReport({ user }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filterType, setFilterType] = useState("month");
  const [filters, setFilters] = useState({
    start_date: "",
    end_date: "",
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear()
  });

  const fetchReport = async () => {
    try {
      setLoading(true);
      let queryParams = {};
      
      if (filterType === "date_range" && filters.start_date && filters.end_date) {
        queryParams.start_date = filters.start_date;
        queryParams.end_date = filters.end_date;
      } else if (filterType === "month" && filters.month && filters.year) {
        queryParams.month = filters.month;
        queryParams.year = filters.year;
      } else if (filterType === "year" && filters.year) {
        queryParams.year = filters.year;
      }
      
      const queryString = new URLSearchParams(queryParams).toString();
      const url = queryString ? `/cash-register/consolidated-report?${queryString}` : '/cash-register/consolidated-report';
      
      const response = await axiosInstance.get(url);
      setReport(response.data);
    } catch (error) {
      console.error("Error fetching consolidated report:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.role === "master") {
      fetchReport();
    }
  }, [filterType, filters, user]);

  const formatCurrency = (amount, currency = "colones") => {
    const symbol = currency === "colones" ? "₡" : "$";
    return `${symbol}${amount.toLocaleString('es-CR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  if (user?.role !== "master") {
    return (
      <div className="text-center py-20">
        <p className="text-slate-600">Solo el usuario Master puede acceder a este reporte</p>
      </div>
    );
  }

  if (loading) {
    return <div className="text-center py-20">Cargando reporte consolidado...</div>;
  }

  const currentYear = new Date().getFullYear();
  const years = Array.from({length: 5}, (_, i) => currentYear - i);
  const months = [
    {value: "1", label: "Enero"}, {value: "2", label: "Febrero"}, {value: "3", label: "Marzo"},
    {value: "4", label: "Abril"}, {value: "5", label: "Mayo"}, {value: "6", label: "Junio"},
    {value: "7", label: "Julio"}, {value: "8", label: "Agosto"}, {value: "9", label: "Septiembre"},
    {value: "10", label: "Octubre"}, {value: "11", label: "Noviembre"}, {value: "12", label: "Diciembre"}
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-slate-800" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          Reporte Consolidado
        </h1>
        <p className="text-slate-600">Resumen de todas las cajas por período</p>
      </div>

      {/* Filters Section */}
      <Card className="bg-white/80 backdrop-blur-xl border-slate-200 shadow-lg">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Calendar className="w-5 h-5" />
            Filtros de Período
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label>Tipo de Filtro</Label>
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="month">Por Mes</SelectItem>
                  <SelectItem value="year">Por Año</SelectItem>
                  <SelectItem value="date_range">Rango de Fechas</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {filterType === "month" && (
              <>
                <div>
                  <Label>Mes</Label>
                  <Select 
                    value={filters.month.toString()} 
                    onValueChange={(val) => setFilters({...filters, month: parseInt(val)})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {months.map(m => (
                        <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Año</Label>
                  <Select 
                    value={filters.year.toString()} 
                    onValueChange={(val) => setFilters({...filters, year: parseInt(val)})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {years.map(y => (
                        <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}

            {filterType === "year" && (
              <div>
                <Label>Año</Label>
                <Select 
                  value={filters.year.toString()} 
                  onValueChange={(val) => setFilters({...filters, year: parseInt(val)})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {years.map(y => (
                      <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {filterType === "date_range" && (
              <>
                <div>
                  <Label>Fecha Inicial</Label>
                  <Input 
                    type="date" 
                    value={filters.start_date}
                    onChange={(e) => setFilters({...filters, start_date: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Fecha Final</Label>
                  <Input 
                    type="date" 
                    value={filters.end_date}
                    onChange={(e) => setFilters({...filters, end_date: e.target.value})}
                  />
                </div>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Totals Summary */}
      {report && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-blue-700 flex items-center gap-2">
                  <Building2 className="w-4 h-4" />
                  Total Cajas
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold text-blue-800">{report.totals.boxes_count}</p>
                <p className="text-xs text-blue-600">{report.totals.registers_count} cierres</p>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-green-700 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4" />
                  Ventas Totales
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold text-green-800">
                  {formatCurrency(report.totals.total_sales_colones)}
                </p>
                <p className="text-sm text-green-600">
                  {formatCurrency(report.totals.total_sales_dollars, "dollars")}
                </p>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-purple-700 flex items-center gap-2">
                  <DollarSign className="w-4 h-4" />
                  Comisión 7.5%
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold text-purple-800">
                  {formatCurrency(report.totals.total_comision_colones)}
                </p>
                <p className="text-sm text-purple-600">
                  {formatCurrency(report.totals.total_comision_dollars, "dollars")}
                </p>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-orange-700 flex items-center gap-2">
                  <DollarSign className="w-4 h-4" />
                  Efectivo Total
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold text-orange-800">
                  {formatCurrency(report.totals.total_cash_colones)}
                </p>
                <p className="text-sm text-orange-600">
                  {formatCurrency(report.totals.total_cash_dollars, "dollars")}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Boxes Breakdown */}
          <Card className="bg-white/80 backdrop-blur-xl border-slate-200 shadow-lg">
            <CardHeader>
              <CardTitle>Desglose por Caja</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {report.boxes.map((box) => (
                  <div key={box.box_id} className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-semibold text-lg text-slate-800">{box.box_name}</h3>
                      <span className="text-sm text-slate-500">{box.registers_count} cierres</span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="text-slate-600">Ventas</p>
                        <p className="font-semibold text-green-700">{formatCurrency(box.total_sales_colones)}</p>
                        <p className="text-xs text-slate-500">{formatCurrency(box.total_sales_dollars, "dollars")}</p>
                      </div>
                      <div>
                        <p className="text-slate-600">Comisión 7.5%</p>
                        <p className="font-semibold text-purple-700">{formatCurrency(box.comision_colones)}</p>
                        <p className="text-xs text-slate-500">{formatCurrency(box.comision_dollars, "dollars")}</p>
                      </div>
                      <div>
                        <p className="text-slate-600">Efectivo Final</p>
                        <p className="font-semibold text-blue-700">{formatCurrency(box.total_cash_colones)}</p>
                        <p className="text-xs text-slate-500">{formatCurrency(box.total_cash_dollars, "dollars")}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {!report && !loading && (
        <Card className="bg-white/80 backdrop-blur-xl border-slate-200 shadow-lg">
          <CardContent className="py-12">
            <p className="text-center text-slate-500">No hay datos para el período seleccionado</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
